/*
 * Contrato de bordado publicado pelo Live Product Options — ADR-0017.
 *
 * O app é a fonte de verdade de quem aceita bordado, com que campos, cores,
 * fontes e prazo. O tema desenha a UI; o app diz o que ela contém. Dois
 * consumidores hoje: a grade de kit (ADR-0012) e a PDP.
 *
 * Recebe o objeto do app por argumento em vez de ler o global, para que o
 * contrato seja testável sem navegador.
 */

(function (window, document) {
  'use strict';

  /**
   * Lê do Live Product Options quais produtos aceitam bordado e como o modal
   * deve ser montado. Um produto sem option set de personalização no app é um
   * produto que não recebe bordado.
   *
   * @param {object|undefined} cloudlift Normalmente `window.Cloudlift`.
   * @returns {{handles: Set<string>, cores: object[], fonte: object|null, campos: object[], aviso: string}|null}
   */
  // O app chama de opção tudo que desenha na PDP, variante inclusive. Estes são
  // os tipos que pertencem ao bordado, e só eles.
  const TIPOS_DE_BORDADO = ['personalize', 'text', 'color-swatch', 'font'];

  function lerContrato(cloudlift) {
    const sets = cloudlift?.options?.config?.configs;
    if (!Array.isArray(sets)) return null;

    const comBordado = sets.filter((set) =>
      (set.options ?? []).some((opcao) => opcao.type === 'personalize')
    );
    if (comBordado.length === 0) return null;

    const referencia = comBordado[0];
    const porTipo = (tipo) => (referencia.options ?? []).find((o) => o.type === tipo);
    const fonte = porTipo('font');
    const familias = fonte?.params?.fontFamilies ?? [];

    return {
      handles: new Set(comBordado.map((set) => set.product?.handle).filter(Boolean)),
      cores: porTipo('color-swatch')?.values ?? [],
      fonte: familias.length ? { nome: fonte.name, rotulo: fonte.label || fonte.name, familias } : null,
      campos: (referencia.options ?? [])
        .filter((opcao) => opcao.type === 'text')
        .map((opcao) => ({ nome: opcao.name, rotulo: opcao.label, limite: opcao.params?.max ?? 30 })),
      aviso: (porTipo('html')?.params?.html ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim(),
      // Nomes das opções que o app desenha para bordado. Quem monta o modal e
      // quem esconde a versão do app leem daqui, para não existirem duas
      // listas que possam divergir.
      // Quanto o bordado custa, em centavos. O app publica em reais, no valor
      // afirmativo da opção de personalização — e é o que o botão precisa
      // mostrar, porque era o que o botão do app mostrava.
      preco: Math.round(((porTipo('personalize')?.values ?? []).find((v) => v.price > 0)?.price ?? 0) * 100),
      opcoes: (referencia.options ?? [])
        .filter((opcao) => TIPOS_DE_BORDADO.includes(opcao.type))
        .map((opcao) => opcao.name)
        .filter(Boolean),
    };
  }

  /**
   * Decide se uma opção desenhada pelo app é de bordado — e portanto deve sair
   * da tela quando o tema assume a personalização.
   *
   * A regra tem duas partes, e a segunda é a que protege a loja: opção que
   * carrega controle de variante (o app prefixa com `v_`) NUNCA é suprimida,
   * mesmo que o nome bata. Esconder o seletor de tamanho tiraria a peça de
   * venda, o que é muito pior que mostrar dois botões de bordado.
   *
   * @param {string} nome Nome da opção, como o app publica.
   * @param {string[]} controles Atributos `name` dos controles dentro dela.
   * @param {object|null} contrato Devolvido por `lerContrato`.
   * @returns {boolean}
   */
  function ehOpcaoDeBordado(nome, controles, contrato) {
    if (!contrato || !nome) return false;
    if ((controles ?? []).some((n) => String(n ?? '').startsWith('v_'))) return false;
    return contrato.opcoes.includes(nome);
  }

  /**
   * Carrega, uma vez só, as famílias de bordado para que cada opção do seletor
   * apareça na própria tipografia. O endpoint de CSS do Live Product Options não
   * serve: ele devolve `http://fonts.gstatic.com`, que a loja em https bloqueia
   * como mixed content.
   * @param {string[]} familias
   */
  function carregarFontes(familias) {
    const id = 'bordado-fontes';
    if (document.getElementById(id)) return;

    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?${familias
      .map((familia) => `family=${encodeURIComponent(familia).replace(/%20/g, '+')}`)
      .join('&')}&display=swap`;

    // Declarar a família na `<option>` não basta: o navegador só baixa a fonte
    // que ele vê renderizada, e a lista fechada do select não conta. Sem este
    // empurrão, só a família pré-selecionada aparece na própria tipografia.
    link.addEventListener('load', () =>
      familias.forEach((familia) => document.fonts.load(`16px '${familia}'`))
    );

    document.head.appendChild(link);
  }

  /**
   * Decide se uma opção de variante desenhada pelo app é repetição do que o
   * tema já oferece.
   *
   * O app injeta o próprio seletor de tamanho mesmo quando o tema tem o dele,
   * e os dois não se falam: medido na loja, mexer no do app não muda a
   * variante que vai para o carrinho, e mexer no do tema não move o do app.
   * A cliente vê "P" num e "M" no outro sem que isso signifique nada.
   *
   * A supressão exige a confirmação de que o tema oferece a mesma opção. Sem
   * essa prova o seletor do app fica: um tamanho duplicado é feio, um produto
   * sem como escolher tamanho não vende.
   *
   * @param {string[]} controles Atributos `name` dos controles da opção.
   * @param {boolean} temEquivalenteNoTema
   * @returns {boolean}
   */
  function ehVarianteDuplicada(controles, temEquivalenteNoTema) {
    const ehVariante = (controles ?? []).some((n) => String(n ?? '').startsWith('v_'));

    return Boolean(ehVariante && temEquivalenteNoTema);
  }

  window.FoxTheme = window.FoxTheme || {};
  window.FoxTheme.bordadoContrato = { lerContrato, carregarFontes, ehOpcaoDeBordado, ehVarianteDuplicada };
})(window, document);
